import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Badge } from "./ui/badge";
import {
  Paintbrush, Pencil, Eraser, PaintBucket, Undo2, Redo2,
  X, Save, Eye, EyeOff, Download, Shovel, Highlighter,
  Palette, Pipette, RotateCcw, ZoomIn, ZoomOut, Move,
  Leaf, ChevronDown, Sparkles, BookOpen, Sun, Moon
} from "lucide-react";

// Tropical/Garden Color Palettes
const COLOR_PALETTES = {
  tropical: {
    name: "Tropical",
    colors: ["#2D8B4E", "#6FCF97", "#F5A623", "#FFD966", "#8E44AD", "#E74C3C", "#3498DB", "#1ABC9C"]
  },
  earth: {
    name: "Earth Tones",
    colors: ["#8B4513", "#A0522D", "#D2691E", "#CD853F", "#DEB887", "#F5DEB3", "#556B2F", "#6B8E23"]
  },
  floral: {
    name: "Floral",
    colors: ["#FF69B4", "#FF1493", "#DB7093", "#FFB6C1", "#FFC0CB", "#9370DB", "#BA55D3", "#DA70D6"]
  },
  foliage: {
    name: "Foliage",
    colors: ["#228B22", "#32CD32", "#90EE90", "#98FB98", "#00FF7F", "#3CB371", "#2E8B57", "#006400"]
  },
  water: {
    name: "Water & Sky",
    colors: ["#87CEEB", "#87CEFA", "#00BFFF", "#1E90FF", "#4169E1", "#6495ED", "#7B68EE", "#ADD8E6"]
  }
};

// Plant icons for shovel tool
const PLANT_OPTIONS = [
  { icon: "🌻", name: "Sunflower" },
  { icon: "🌹", name: "Rose" },
  { icon: "🌷", name: "Tulip" },
  { icon: "🌺", name: "Hibiscus" },
  { icon: "🌸", name: "Cherry Blossom" },
  { icon: "🌼", name: "Daisy" },
  { icon: "🌱", name: "Seedling" },
  { icon: "🌿", name: "Herb" },
  { icon: "🍀", name: "Clover" },
  { icon: "🌴", name: "Palm" },
  { icon: "🌵", name: "Cactus" },
  { icon: "🍅", name: "Tomato" },
  { icon: "🥕", name: "Carrot" },
  { icon: "🥬", name: "Lettuce" },
  { icon: "🌽", name: "Corn" },
  { icon: "🫑", name: "Pepper" }
];

const TOOLS = {
  BRUSH: "brush",
  PENCIL: "pencil",
  ERASER: "eraser",
  FILL: "fill",
  SHOVEL: "shovel",
  HIGHLIGHT: "highlight",
  EYEDROPPER: "eyedropper",
  MOVE: "move",
  ZEN_FILL: "zen_fill"
};

// Coloring book color schemes
const COLORING_SCHEMES = {
  classic: { name: "Classic", lineColor: "#1a1a1a", bgColor: "#ffffff" },
  sepia: { name: "Sepia", lineColor: "#5c4033", bgColor: "#f5f0e6" },
  blueprint: { name: "Blueprint", lineColor: "#ffffff", bgColor: "#1e3a5f" },
  nature: { name: "Nature", lineColor: "#2d5016", bgColor: "#f8fdf5" }
};

// Tool button component - defined outside to avoid re-render issues
const ToolButton = ({ toolType, icon: Icon, label, isActive, onClick }) => (
  <Button
    variant={isActive ? "default" : "outline"}
    size="sm"
    onClick={onClick}
    className={`flex flex-col items-center gap-1 h-auto py-2 px-3 ${
      isActive ? "bg-green-600 hover:bg-green-700 text-white" : ""
    }`}
    title={label}
  >
    <Icon className="w-5 h-5" />
    <span className="text-xs">{label}</span>
  </Button>
);

const RenderPaintEditor = ({ 
  imageUrl, 
  onSave, 
  onClose, 
  designName = "Garden" 
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState(TOOLS.BRUSH);
  const [color, setColor] = useState("#2D8B4E");
  const [brushSize, setBrushSize] = useState(10);
  const [opacity, setOpacity] = useState(100);
  const [showOriginal, setShowOriginal] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [extractedColors, setExtractedColors] = useState([]);
  const [selectedPalette, setSelectedPalette] = useState("tropical");
  const [showPaletteDropdown, setShowPaletteDropdown] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(PLANT_OPTIONS[0]);
  const [showPlantPicker, setShowPlantPicker] = useState(false);
  const [placedPlants, setPlacedPlants] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  
  // Coloring Book Mode State
  const [coloringMode, setColoringMode] = useState(false);
  const [coloringScheme, setColoringScheme] = useState("classic");
  const [outlineCanvas, setOutlineCanvas] = useState(null);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [zenRevealAmount, setZenRevealAmount] = useState(0);
  const [coloringProgress, setColoringProgress] = useState(0);
  const outlineCanvasRef = useRef(null);

  // Load image and extract colors
  useEffect(() => {
    if (!imageUrl) return;
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Set canvas size based on image
      const maxWidth = Math.min(window.innerWidth - 400, 1200);
      const maxHeight = window.innerHeight - 200;
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      
      setCanvasSize({
        width: img.width * scale,
        height: img.height * scale
      });
      
      // Draw image on canvas
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Extract colors from image
        extractColorsFromImage(ctx, canvas.width, canvas.height);
        
        // Save initial state
        saveToHistory();
        setImageLoaded(true);
      }
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Extract dominant colors from image
  const extractColorsFromImage = (ctx, width, height) => {
    try {
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      const colorCounts = {};
      
      // Sample every 10th pixel for performance
      for (let i = 0; i < data.length; i += 40) {
        const r = Math.round(data[i] / 32) * 32;
        const g = Math.round(data[i + 1] / 32) * 32;
        const b = Math.round(data[i + 2] / 32) * 32;
        const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        colorCounts[hex] = (colorCounts[hex] || 0) + 1;
      }
      
      // Get top 8 colors
      const sortedColors = Object.entries(colorCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([color]) => color);
      
      setExtractedColors(sortedColors);
    } catch (e) {
      console.error("Could not extract colors:", e);
    }
  };

  // Generate coloring book outline from image using edge detection
  const generateColoringOutline = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImageData) return;
    
    const ctx = canvas.getContext("2d");
    const scheme = COLORING_SCHEMES[coloringScheme];
    const width = canvas.width;
    const height = canvas.height;
    
    // Get original image data
    const srcData = originalImageData.data;
    
    // Create output image data
    const outputData = ctx.createImageData(width, height);
    const dst = outputData.data;
    
    // Parse colors
    const lineR = parseInt(scheme.lineColor.slice(1, 3), 16);
    const lineG = parseInt(scheme.lineColor.slice(3, 5), 16);
    const lineB = parseInt(scheme.lineColor.slice(5, 7), 16);
    const bgR = parseInt(scheme.bgColor.slice(1, 3), 16);
    const bgG = parseInt(scheme.bgColor.slice(3, 5), 16);
    const bgB = parseInt(scheme.bgColor.slice(5, 7), 16);
    
    // Sobel edge detection kernels
    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    
    // Convert to grayscale and detect edges
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0, gy = 0;
        
        // Apply Sobel kernels
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4;
            // Grayscale value
            const gray = (srcData[idx] * 0.299 + srcData[idx + 1] * 0.587 + srcData[idx + 2] * 0.114);
            const ki = (ky + 1) * 3 + (kx + 1);
            gx += gray * sobelX[ki];
            gy += gray * sobelY[ki];
          }
        }
        
        // Calculate edge magnitude
        const magnitude = Math.sqrt(gx * gx + gy * gy);
        const idx = (y * width + x) * 4;
        
        // Threshold for edge detection (lower = more detail)
        const threshold = 30;
        const isEdge = magnitude > threshold;
        
        if (isEdge) {
          // Draw line color
          dst[idx] = lineR;
          dst[idx + 1] = lineG;
          dst[idx + 2] = lineB;
          dst[idx + 3] = 255;
        } else {
          // Draw background color
          dst[idx] = bgR;
          dst[idx + 1] = bgG;
          dst[idx + 2] = bgB;
          dst[idx + 3] = 255;
        }
      }
    }
    
    // Handle edges of image
    for (let x = 0; x < width; x++) {
      const topIdx = x * 4;
      const bottomIdx = ((height - 1) * width + x) * 4;
      dst[topIdx] = dst[bottomIdx] = bgR;
      dst[topIdx + 1] = dst[bottomIdx + 1] = bgG;
      dst[topIdx + 2] = dst[bottomIdx + 2] = bgB;
      dst[topIdx + 3] = dst[bottomIdx + 3] = 255;
    }
    for (let y = 0; y < height; y++) {
      const leftIdx = y * width * 4;
      const rightIdx = (y * width + width - 1) * 4;
      dst[leftIdx] = dst[rightIdx] = bgR;
      dst[leftIdx + 1] = dst[rightIdx + 1] = bgG;
      dst[leftIdx + 2] = dst[rightIdx + 2] = bgB;
      dst[leftIdx + 3] = dst[rightIdx + 3] = 255;
    }
    
    // Store the outline data
    setOutlineCanvas(outputData);
    
    // Draw the outline to canvas
    ctx.putImageData(outputData, 0, 0);
    saveToHistory();
  }, [coloringScheme, originalImageData, saveToHistory]);

  // Toggle coloring mode
  const toggleColoringMode = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    
    if (!coloringMode) {
      // Entering coloring mode - store original and generate outline
      if (!originalImageData) {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setOriginalImageData(imgData);
      }
      setColoringMode(true);
      setTimeout(() => generateColoringOutline(), 100);
    } else {
      // Exiting coloring mode - restore to current painted state
      setColoringMode(false);
      setZenRevealAmount(0);
    }
  }, [coloringMode, originalImageData, generateColoringOutline]);

  // Zen reveal - gradually show original colors
  const applyZenReveal = useCallback((amount) => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImageData || !outlineCanvas) return;
    
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    
    // Get current canvas state (with user's coloring)
    const currentData = ctx.getImageData(0, 0, width, height);
    const current = currentData.data;
    const original = originalImageData.data;
    const outline = outlineCanvas.data;
    
    // Blend based on reveal amount
    const blend = amount / 100;
    
    for (let i = 0; i < current.length; i += 4) {
      // Check if this pixel is part of the outline (black line)
      const isOutline = outline[i] < 50 && outline[i + 1] < 50 && outline[i + 2] < 50;
      
      if (!isOutline) {
        // Only reveal non-outline areas
        current[i] = Math.round(current[i] * (1 - blend) + original[i] * blend);
        current[i + 1] = Math.round(current[i + 1] * (1 - blend) + original[i + 1] * blend);
        current[i + 2] = Math.round(current[i + 2] * (1 - blend) + original[i + 2] * blend);
      }
    }
    
    ctx.putImageData(currentData, 0, 0);
  }, [originalImageData, outlineCanvas]);

  // Calculate coloring progress
  const calculateColoringProgress = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !outlineCanvas) return;
    
    const ctx = canvas.getContext("2d");
    const currentData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const current = currentData.data;
    const outline = outlineCanvas.data;
    const scheme = COLORING_SCHEMES[coloringScheme];
    
    const bgR = parseInt(scheme.bgColor.slice(1, 3), 16);
    const bgG = parseInt(scheme.bgColor.slice(3, 5), 16);
    const bgB = parseInt(scheme.bgColor.slice(5, 7), 16);
    
    let totalFillable = 0;
    let filled = 0;
    
    for (let i = 0; i < current.length; i += 4) {
      // Check if this is a fillable area (not an outline)
      const isOutline = outline[i] < 100 && outline[i + 1] < 100 && outline[i + 2] < 100;
      
      if (!isOutline) {
        totalFillable++;
        // Check if it's been colored (different from background)
        const isBg = Math.abs(current[i] - bgR) < 10 && 
                     Math.abs(current[i + 1] - bgG) < 10 && 
                     Math.abs(current[i + 2] - bgB) < 10;
        if (!isBg) filled++;
      }
    }
    
    const progress = totalFillable > 0 ? Math.round((filled / totalFillable) * 100) : 0;
    setColoringProgress(progress);
  }, [outlineCanvas, coloringScheme]);

  // Update zen reveal when slider changes
  useEffect(() => {
    if (coloringMode && zenRevealAmount > 0) {
      applyZenReveal(zenRevealAmount);
    }
  }, [zenRevealAmount, coloringMode, applyZenReveal]);

  // Regenerate outline when scheme changes
  useEffect(() => {
    if (coloringMode && originalImageData) {
      generateColoringOutline();
    }
  }, [coloringScheme, coloringMode, originalImageData, generateColoringOutline]);

  // Calculate progress after coloring actions
  useEffect(() => {
    if (coloringMode) {
      const timer = setTimeout(calculateColoringProgress, 500);
      return () => clearTimeout(timer);
    }
  }, [historyIndex, coloringMode, calculateColoringProgress]);

  // Save current canvas state to history
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const imageData = canvas.toDataURL();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({
      imageData,
      plants: [...placedPlants],
      highlights: [...highlights]
    });
    
    // Keep only last 30 states
    if (newHistory.length > 30) newHistory.shift();
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, placedPlants, highlights]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(img, 0, 0);
        }
      };
      img.src = state.imageData;
      
      setPlacedPlants(state.plants || []);
      setHighlights(state.highlights || []);
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const state = history[newIndex];
      
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(img, 0, 0);
        }
      };
      img.src = state.imageData;
      
      setPlacedPlants(state.plants || []);
      setHighlights(state.highlights || []);
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  // Get position from mouse/touch event
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  // Drawing functions
  const startDrawing = (e) => {
    e.preventDefault();
    const pos = getPosition(e);
    
    if (tool === TOOLS.MOVE) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY });
      return;
    }
    
    if (tool === TOOLS.EYEDROPPER) {
      pickColor(pos);
      return;
    }
    
    if (tool === TOOLS.FILL || tool === TOOLS.ZEN_FILL) {
      floodFill(pos);
      return;
    }
    
    if (tool === TOOLS.SHOVEL) {
      placePlant(pos);
      return;
    }
    
    if (tool === TOOLS.HIGHLIGHT) {
      addHighlight(pos);
      return;
    }
    
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const draw = (e) => {
    e.preventDefault();
    
    if (tool === TOOLS.MOVE && isPanning) {
      const clientX = e.clientX || e.touches?.[0]?.clientX;
      const clientY = e.clientY || e.touches?.[0]?.clientY;
      setPan({
        x: pan.x + (clientX - lastPanPoint.x),
        y: pan.y + (clientY - lastPanPoint.y)
      });
      setLastPanPoint({ x: clientX, y: clientY });
      return;
    }
    
    if (!isDrawing) return;
    
    const pos = getPosition(e);
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    
    ctx.globalAlpha = opacity / 100;
    
    if (tool === TOOLS.ERASER) {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = brushSize * 2;
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = color;
      ctx.lineWidth = tool === TOOLS.PENCIL ? Math.max(1, brushSize / 3) : brushSize;
    }
    
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveToHistory();
    }
    setIsPanning(false);
  };

  // Flood fill (paint bucket)
  const floodFill = (pos) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    const startX = Math.floor(pos.x);
    const startY = Math.floor(pos.y);
    const startIdx = (startY * canvas.width + startX) * 4;
    
    const startR = data[startIdx];
    const startG = data[startIdx + 1];
    const startB = data[startIdx + 2];
    
    // Parse fill color
    const fillR = parseInt(color.slice(1, 3), 16);
    const fillG = parseInt(color.slice(3, 5), 16);
    const fillB = parseInt(color.slice(5, 7), 16);
    
    // Don't fill if same color
    if (startR === fillR && startG === fillG && startB === fillB) return;
    
    const tolerance = 32;
    const stack = [[startX, startY]];
    const visited = new Set();
    
    const matchColor = (idx) => {
      return Math.abs(data[idx] - startR) <= tolerance &&
             Math.abs(data[idx + 1] - startG) <= tolerance &&
             Math.abs(data[idx + 2] - startB) <= tolerance;
    };
    
    while (stack.length > 0 && stack.length < 100000) {
      const [x, y] = stack.pop();
      const key = `${x},${y}`;
      
      if (visited.has(key)) continue;
      if (x < 0 || x >= canvas.width || y < 0 || y >= canvas.height) continue;
      
      const idx = (y * canvas.width + x) * 4;
      if (!matchColor(idx)) continue;
      
      visited.add(key);
      
      data[idx] = fillR;
      data[idx + 1] = fillG;
      data[idx + 2] = fillB;
      data[idx + 3] = Math.round(255 * opacity / 100);
      
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    
    ctx.putImageData(imageData, 0, 0);
    saveToHistory();
  };

  // Color picker (eyedropper)
  const pickColor = (pos) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    
    const pixel = ctx.getImageData(Math.floor(pos.x), Math.floor(pos.y), 1, 1).data;
    const hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;
    setColor(hex);
    setTool(TOOLS.BRUSH);
  };

  // Place plant (shovel tool)
  const placePlant = (pos) => {
    const newPlant = {
      id: Date.now(),
      x: pos.x,
      y: pos.y,
      icon: selectedPlant.icon,
      name: selectedPlant.name,
      size: brushSize * 3
    };
    setPlacedPlants([...placedPlants, newPlant]);
    saveToHistory();
  };

  // Add highlight
  const addHighlight = (pos) => {
    const newHighlight = {
      id: Date.now(),
      x: pos.x,
      y: pos.y,
      radius: brushSize * 2,
      color: color
    };
    setHighlights([...highlights, newHighlight]);
    saveToHistory();
  };

  // Remove plant
  const removePlant = (plantId) => {
    setPlacedPlants(placedPlants.filter(p => p.id !== plantId));
    saveToHistory();
  };

  // Remove highlight
  const removeHighlight = (highlightId) => {
    setHighlights(highlights.filter(h => h.id !== highlightId));
    saveToHistory();
  };

  // Reset to original
  const resetToOriginal = () => {
    if (history.length > 0) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(img, 0, 0);
        }
      };
      img.src = history[0].imageData;
      setPlacedPlants([]);
      setHighlights([]);
      setHistoryIndex(0);
    }
  };

  // Save edited image
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Create a composite canvas with plants and highlights
    const compositeCanvas = document.createElement("canvas");
    compositeCanvas.width = canvas.width;
    compositeCanvas.height = canvas.height;
    const ctx = compositeCanvas.getContext("2d");
    
    // Draw the painted canvas
    ctx.drawImage(canvas, 0, 0);
    
    // Draw highlights
    highlights.forEach(h => {
      ctx.beginPath();
      ctx.arc(h.x, h.y, h.radius, 0, Math.PI * 2);
      ctx.strokeStyle = h.color;
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.fillStyle = h.color + "30";
      ctx.fill();
    });
    
    // Draw plants
    ctx.font = "bold 32px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    placedPlants.forEach(p => {
      ctx.font = `${p.size}px Arial`;
      ctx.fillText(p.icon, p.x, p.y);
    });
    
    const dataUrl = compositeCanvas.toDataURL("image/png");
    onSave?.(dataUrl, { plants: placedPlants, highlights });
  };

  // Download image
  const downloadImage = () => {
    handleSave();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement("a");
    link.download = `${designName}-edited.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex"
    >
      {/* Tools Sidebar */}
      <div className="w-72 bg-white border-r flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-green-50 to-amber-50">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <Paintbrush className="w-5 h-5 text-green-600" />
              Edit Render
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tools */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-gray-500 mb-3">TOOLS</h3>
          <div className="grid grid-cols-4 gap-2">
            <ToolButton toolType={TOOLS.BRUSH} icon={Paintbrush} label="Brush" isActive={tool === TOOLS.BRUSH} onClick={() => setTool(TOOLS.BRUSH)} />
            <ToolButton toolType={TOOLS.PENCIL} icon={Pencil} label="Pencil" isActive={tool === TOOLS.PENCIL} onClick={() => setTool(TOOLS.PENCIL)} />
            <ToolButton toolType={TOOLS.ERASER} icon={Eraser} label="Eraser" isActive={tool === TOOLS.ERASER} onClick={() => setTool(TOOLS.ERASER)} />
            <ToolButton toolType={TOOLS.FILL} icon={PaintBucket} label="Fill" isActive={tool === TOOLS.FILL || tool === TOOLS.ZEN_FILL} onClick={() => setTool(coloringMode ? TOOLS.ZEN_FILL : TOOLS.FILL)} />
            <ToolButton toolType={TOOLS.SHOVEL} icon={Shovel} label="Plant" isActive={tool === TOOLS.SHOVEL} onClick={() => setTool(TOOLS.SHOVEL)} />
            <ToolButton toolType={TOOLS.HIGHLIGHT} icon={Highlighter} label="Highlight" isActive={tool === TOOLS.HIGHLIGHT} onClick={() => setTool(TOOLS.HIGHLIGHT)} />
            <ToolButton toolType={TOOLS.EYEDROPPER} icon={Pipette} label="Pick" isActive={tool === TOOLS.EYEDROPPER} onClick={() => setTool(TOOLS.EYEDROPPER)} />
            <ToolButton toolType={TOOLS.MOVE} icon={Move} label="Pan" isActive={tool === TOOLS.MOVE} onClick={() => setTool(TOOLS.MOVE)} />
          </div>
        </div>

        {/* Coloring Book Mode */}
        <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-purple-600" />
              Coloring Book
            </h3>
            <Button
              size="sm"
              variant={coloringMode ? "default" : "outline"}
              onClick={toggleColoringMode}
              className={coloringMode ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
              data-testid="coloring-mode-toggle"
            >
              {coloringMode ? "Exit" : "Enter"} Mode
            </Button>
          </div>
          
          {coloringMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              {/* Coloring Scheme Selector */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">LINE STYLE</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(COLORING_SCHEMES).map(([key, scheme]) => (
                    <button
                      key={key}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        coloringScheme === key 
                          ? "border-purple-500 bg-purple-50" 
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                      onClick={() => setColoringScheme(key)}
                      data-testid={`coloring-scheme-${key}`}
                    >
                      <div 
                        className="w-full h-6 rounded mb-1"
                        style={{ 
                          backgroundColor: scheme.bgColor,
                          border: `2px solid ${scheme.lineColor}`
                        }}
                      />
                      <span className="text-xs">{scheme.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Progress Indicator */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-gray-500">PROGRESS</p>
                  <Badge variant="secondary" className="text-xs">
                    {coloringProgress}%
                  </Badge>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${coloringProgress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
              
              {/* Zen Reveal Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    ZEN REVEAL
                  </p>
                  <span className="text-xs text-purple-600">{zenRevealAmount}%</span>
                </div>
                <Slider
                  value={[zenRevealAmount]}
                  onValueChange={([v]) => setZenRevealAmount(v)}
                  min={0}
                  max={100}
                  step={5}
                  className="flex-1"
                  data-testid="zen-reveal-slider"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Peek at original colors while you color
                </p>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={generateColoringOutline}
                  data-testid="regenerate-outline-btn"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset Outline
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={() => setZenRevealAmount(100)}
                  data-testid="reveal-all-btn"
                >
                  <Sun className="w-3 h-3 mr-1" />
                  Reveal All
                </Button>
              </div>
            </motion.div>
          )}
          
          {!coloringMode && (
            <p className="text-xs text-gray-500">
              Convert to a coloring page with black & white outlines. Tap to fill sections with color!
            </p>
          )}
        </div>

        {/* Brush Settings */}
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-gray-500 mb-3">BRUSH SIZE</h3>
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-full border-2 flex items-center justify-center"
              style={{ 
                width: Math.min(brushSize + 10, 40), 
                height: Math.min(brushSize + 10, 40),
                backgroundColor: tool === TOOLS.ERASER ? "#f0f0f0" : color
              }}
            />
            <Slider
              value={[brushSize]}
              onValueChange={([v]) => setBrushSize(v)}
              min={1}
              max={50}
              step={1}
              className="flex-1"
            />
            <span className="text-sm font-mono w-8">{brushSize}</span>
          </div>
          
          <h3 className="text-sm font-semibold text-gray-500 mt-4 mb-3">OPACITY</h3>
          <div className="flex items-center gap-3">
            <Slider
              value={[opacity]}
              onValueChange={([v]) => setOpacity(v)}
              min={10}
              max={100}
              step={5}
              className="flex-1"
            />
            <span className="text-sm font-mono w-10">{opacity}%</span>
          </div>
        </div>

        {/* Plant Picker (for Shovel tool) */}
        {tool === TOOLS.SHOVEL && (
          <div className="p-4 border-b">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">SELECT PLANT</h3>
            <div className="relative">
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowPlantPicker(!showPlantPicker)}
              >
                <span className="flex items-center gap-2">
                  <span className="text-2xl">{selectedPlant.icon}</span>
                  {selectedPlant.name}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {showPlantPicker && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-1 p-2">
                    {PLANT_OPTIONS.map((plant) => (
                      <button
                        key={plant.name}
                        className={`p-2 text-2xl rounded hover:bg-green-50 ${
                          selectedPlant.name === plant.name ? "bg-green-100" : ""
                        }`}
                        onClick={() => {
                          setSelectedPlant(plant);
                          setShowPlantPicker(false);
                        }}
                        title={plant.name}
                      >
                        {plant.icon}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Color Palette */}
        <div className="p-4 border-b flex-1 overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-500 mb-3">COLOR</h3>
          
          {/* Current Color & Picker */}
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-lg border-2 shadow-inner"
              style={{ backgroundColor: color }}
            />
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-10 rounded cursor-pointer"
            />
          </div>
          
          {/* Extracted Colors */}
          {extractedColors.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-400 mb-2">FROM IMAGE</p>
              <div className="flex flex-wrap gap-1">
                {extractedColors.map((c, i) => (
                  <button
                    key={i}
                    className={`w-7 h-7 rounded border-2 ${color === c ? "border-gray-800 ring-2 ring-green-400" : "border-gray-200"}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Preset Palettes */}
          <div className="relative mb-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-between"
              onClick={() => setShowPaletteDropdown(!showPaletteDropdown)}
            >
              <span className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                {COLOR_PALETTES[selectedPalette].name}
              </span>
              <ChevronDown className="w-4 h-4" />
            </Button>
            
            {showPaletteDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10">
                {Object.entries(COLOR_PALETTES).map(([key, palette]) => (
                  <button
                    key={key}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      selectedPalette === key ? "bg-green-50" : ""
                    }`}
                    onClick={() => {
                      setSelectedPalette(key);
                      setShowPaletteDropdown(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {palette.colors.slice(0, 4).map((c, i) => (
                          <div 
                            key={i} 
                            className="w-4 h-4 -ml-1 first:ml-0 rounded-full border border-white"
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      {palette.name}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap gap-1">
            {COLOR_PALETTES[selectedPalette].colors.map((c, i) => (
              <button
                key={i}
                className={`w-7 h-7 rounded border-2 ${color === c ? "border-gray-800 ring-2 ring-green-400" : "border-gray-200"}`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 space-y-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={undo}
              disabled={historyIndex <= 0}
              className="flex-1"
            >
              <Undo2 className="w-4 h-4 mr-1" /> Undo
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="flex-1"
            >
              <Redo2 className="w-4 h-4 mr-1" /> Redo
            </Button>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOriginal(!showOriginal)}
            className="w-full"
          >
            {showOriginal ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showOriginal ? "Show Edited" : "Show Original"}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={resetToOriginal}
            className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Reset to Original
          </Button>
          
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={downloadImage}
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-1" /> Download
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div 
        ref={containerRef}
        className="flex-1 flex items-center justify-center bg-gray-900 overflow-hidden relative"
      >
        {/* Zoom Controls */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setZoom(Math.max(25, zoom - 25))}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Badge variant="secondary" className="px-3">
            {zoom}%
          </Badge>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setZoom(Math.min(200, zoom + 25))}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>

        {/* Canvas Container */}
        <div
          style={{
            transform: `scale(${zoom / 100}) translate(${pan.x}px, ${pan.y}px)`,
            transition: isPanning ? "none" : "transform 0.1s ease"
          }}
        >
          <div className="relative">
            {/* Main Canvas */}
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className={`rounded-lg shadow-2xl ${
                tool === TOOLS.MOVE ? "cursor-grab" : 
                tool === TOOLS.EYEDROPPER ? "cursor-crosshair" : 
                "cursor-crosshair"
              } ${showOriginal ? "opacity-0" : ""}`}
              style={{
                touchAction: "none",
                maxWidth: "100%",
                maxHeight: "calc(100vh - 100px)"
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            
            {/* Original Image Overlay (for toggle) */}
            {showOriginal && history.length > 0 && (
              <img
                src={history[0]?.imageData}
                alt="Original"
                className="absolute top-0 left-0 rounded-lg"
                style={{ width: canvasSize.width, height: canvasSize.height }}
              />
            )}
            
            {/* Placed Plants Overlay */}
            {!showOriginal && placedPlants.map((plant) => (
              <div
                key={plant.id}
                className="absolute group"
                style={{
                  left: plant.x * (zoom / 100) - plant.size / 2,
                  top: plant.y * (zoom / 100) - plant.size / 2,
                  fontSize: plant.size,
                  transform: `scale(${100 / zoom})`
                }}
              >
                <span className="select-none">{plant.icon}</span>
                <button
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  onClick={() => removePlant(plant.id)}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            
            {/* Highlights Overlay */}
            {!showOriginal && highlights.map((h) => (
              <div
                key={h.id}
                className="absolute group pointer-events-auto"
                style={{
                  left: h.x * (zoom / 100) - h.radius,
                  top: h.y * (zoom / 100) - h.radius,
                  width: h.radius * 2,
                  height: h.radius * 2,
                  borderRadius: "50%",
                  border: `3px solid ${h.color}`,
                  backgroundColor: `${h.color}20`,
                  transform: `scale(${100 / zoom})`
                }}
              >
                <button
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  onClick={() => removeHighlight(h.id)}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center text-white">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p>Loading render...</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RenderPaintEditor;
